import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { categoryApi } from '@/lib/api';
import { courseKeysApi } from '@/lib/api/courseKeys';
import { uploadCourseKeyInstructionFile } from '@/lib/api/courseKeyInstructionUpload';
import { programsV2Api } from '@/lib/api/v2/programs';
import { cn } from '@/lib/utils';
import type {
  CourseKeyConfig,
  CourseKeyContentTabs,
  CourseKeyFormValues,
  CourseKeyPlatform,
  CourseKeyTimeUnit,
  DeliveryModeV2,
  IspringContentItem,
} from '@/types/course-key';
import {
  resolveAccessPeriod,
  resolveConfigDuration,
  resolveExtensionPeriod,
  resolveInactivePeriod,
} from '@/utils/courseKeyTime';
import { FileUp, Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import {
  MINDTAP_MAX_KEYS,
  normalizeMindtapReleaseRows,
  validateMindtapReleaseRows,
} from '@/lib/mindtapReleaseKeys';
import {
  contentStructuredFromConfig,
  contentTabsFromConfig,
  defaultContentStructured,
  defaultContentTabs,
  emptyContentStructured,
  MINDTAP_COURSE_KEY_TOKEN,
} from '@/lib/course-key-content-defaults';
import {
  CourseKeyAlertBuilder,
  CourseKeyPolicyBuilder,
  CourseKeyStepBuilder,
} from '@/components/course-keys/CourseKeyStructuredBuilders';

const DELIVERY_MODES: { id: DeliveryModeV2; label: string }[] = [
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'via-zoom', label: 'Via-Zoom' },
  { id: 'on-campus', label: 'On-Campus' },
  { id: 'online', label: 'Online' },
];

const MINDTAP_TOKEN = MINDTAP_COURSE_KEY_TOKEN;

function legacyInstructionsFromTabs(
  platform: CourseKeyPlatform,
  tabs: CourseKeyContentTabs
): string {
  if (platform === 'mindtap') return tabs.instructionsHtml;
  return tabs.overviewHtml;
}

const defaultForm: CourseKeyFormValues = {
  platform: 'ispring',
  categoryId: '',
  programId: '',
  deliveryModes: [],
  ispringCourseIds: [],
  contentTabs: defaultContentTabs('ispring'),
  contentStructured: defaultContentStructured('ispring'),
  instructionFiles: [],
  durationValue: 12,
  durationUnit: 'weeks',
  extensionFees: [{ value: 2, unit: 'weeks', amountUsd: 20 }],
  reactivationInactiveValue: 14,
  reactivationInactiveUnit: 'days',
  reactivationAccessValue: 12,
  reactivationAccessUnit: 'weeks',
  reactivationAmountUsd: 25,
  portalUrl: '',
  sharedKeysEnabled: false,
  sharedKeys: [{ name: '', courseKey: '' }],
};

function getContentId(item: IspringContentItem): string {
  return item.contentItemId || item.id || '';
}

function sameCourseIdSet(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const rightIds = new Set(right);
  return left.every((id) => rightIds.has(id));
}

const DELIVERY_MODE_IDS = new Set<DeliveryModeV2>(DELIVERY_MODES.map((mode) => mode.id));

function extractProgramDeliveryModes(
  availableProgramTypes?: Array<{ type?: string }>
): DeliveryModeV2[] {
  const seen = new Set<DeliveryModeV2>();
  const modes: DeliveryModeV2[] = [];
  for (const programType of availableProgramTypes || []) {
    const type = programType.type as DeliveryModeV2;
    if (!DELIVERY_MODE_IDS.has(type) || seen.has(type)) continue;
    seen.add(type);
    modes.push(type);
  }
  return modes;
}

export type CourseKeySaveResult = {
  configId: string;
  platform: CourseKeyPlatform;
  status: 'draft' | 'active';
};

type Props = {
  active: boolean;
  editing?: CourseKeyConfig | null;
  initialPlatform?: CourseKeyPlatform;
  onSaved: () => void;
  onSubmitSuccess: (result: CourseKeySaveResult) => void;
  onCancel?: () => void;
};

function TimePeriodField({
  id,
  label,
  description,
  value,
  unit,
  onValueChange,
  onUnitChange,
}: {
  id: string;
  label: string;
  description?: string;
  value: number;
  unit: CourseKeyTimeUnit;
  onValueChange: (value: number) => void;
  onUnitChange: (unit: CourseKeyTimeUnit) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex max-w-xs gap-2">
        <Input
          id={id}
          type="number"
          min={1}
          className="flex-1"
          value={value}
          onChange={(e) => onValueChange(parseInt(e.target.value, 10) || 1)}
        />
        <Select value={unit} onValueChange={(next) => onUnitChange(next as CourseKeyTimeUnit)}>
          <SelectTrigger className="w-[110px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="days">Days</SelectItem>
            <SelectItem value="weeks">Weeks</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
    </div>
  );
}

function StepSection({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[36px_1fr] gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold">
        {step}
      </div>
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold">{title}</h3>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

export function CourseKeyEditorForm({
  active,
  editing,
  initialPlatform = 'ispring',
  onSaved,
  onSubmitSuccess,
  onCancel,
}: Props) {
  const [form, setForm] = useState<CourseKeyFormValues>({ ...defaultForm, platform: initialPlatform });
  const [categories, setCategories] = useState<
    Array<{ _id: string; title: string; description?: string }>
  >([]);
  const [programs, setPrograms] = useState<
    Array<{ _id: string; title: string; availableDeliveryModes: DeliveryModeV2[] }>
  >([]);
  const [ispringCourses, setIspringCourses] = useState<IspringContentItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingMoreCourses, setLoadingMoreCourses] = useState(false);
  const [ispringCourseSearch, setIspringCourseSearch] = useState('');
  const [programSearch, setProgramSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingCourseUpdateStatus, setPendingCourseUpdateStatus] = useState<
    'draft' | 'active' | null
  >(null);
  const ispringLoadGenerationRef = useRef(0);
  const [setupLocked, setSetupLocked] = useState(false);
  const [uploadingInstructionFile, setUploadingInstructionFile] = useState(false);
  const instructionFileInputRef = useRef<HTMLInputElement>(null);

  const platform = form.platform;
  const setupDisabled = setupLocked;

  const filteredIspringCourses = useMemo(() => {
    const q = ispringCourseSearch.trim().toLowerCase();
    if (!q) return ispringCourses;
    return ispringCourses.filter((course) => {
      const title = (course.title || '').toLowerCase();
      const id = getContentId(course).toLowerCase();
      return title.includes(q) || id.includes(q);
    });
  }, [ispringCourses, ispringCourseSearch]);

  const selectedIspringCourses = useMemo(() => {
    const byId = new Map(
      ispringCourses.map((course) => [getContentId(course), course] as const)
    );
    return form.ispringCourseIds.map((id) => {
      const course = byId.get(id);
      return {
        id,
        title: course?.title?.trim() || id,
      };
    });
  }, [form.ispringCourseIds, ispringCourses]);

  const filteredPrograms = useMemo(() => {
    const q = programSearch.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((program) => program.title.toLowerCase().includes(q));
  }, [programs, programSearch]);

  const selectedProgram = useMemo(
    () => programs.find((program) => program._id === form.programId),
    [programs, form.programId]
  );

  const availableDeliveryModeOptions = useMemo(() => {
    if (!selectedProgram) return [];
    return DELIVERY_MODES.filter((mode) =>
      selectedProgram.availableDeliveryModes.includes(mode.id)
    );
  }, [selectedProgram]);

  useEffect(() => {
    if (!active) {
      setIspringCourseSearch('');
      setProgramSearch('');
      setSetupLocked(false);
      return;
    }
    setSetupLocked(Boolean(editing));
    setForm({
      ...defaultForm,
      platform: editing?.platform ?? initialPlatform,
      ...(editing
        ? {
            platform: editing.platform,
            categoryId:
              typeof editing.categoryId === 'object'
                ? editing.categoryId._id
                : editing.categoryId,
            programId:
              typeof editing.programId === 'object' ? editing.programId._id : editing.programId,
            deliveryModes: editing.deliveryModes,
            ispringCourseIds: editing.ispringCourseIds || [],
            contentTabs: contentTabsFromConfig(editing),
            contentStructured: contentStructuredFromConfig(editing),
            instructionFiles: editing.instructionFiles || [],
            ...(() => {
              const duration = resolveConfigDuration(editing);
              const reactivation = editing.reactivationFee;
              const inactive = reactivation
                ? resolveInactivePeriod(reactivation)
                : { value: 14, unit: 'days' as const };
              const access = reactivation
                ? resolveAccessPeriod(reactivation)
                : duration;
              return {
                durationValue: duration.value,
                durationUnit: duration.unit,
                extensionFees: (editing.extensionFees || []).map((f) => {
                  const period = resolveExtensionPeriod(f);
                  return {
                    value: period.value,
                    unit: period.unit,
                    amountUsd: f.amountCents / 100,
                  };
                }),
                reactivationInactiveValue: inactive.value,
                reactivationInactiveUnit: inactive.unit,
                reactivationAccessValue: access.value,
                reactivationAccessUnit: access.unit,
                reactivationAmountUsd: (reactivation?.amountCents ?? 2500) / 100,
              };
            })(),
            portalUrl: editing.portalUrl || '',
            sharedKeysEnabled: Boolean(editing.sharedKeysEnabled),
            sharedKeys:
              editing.sharedKeys && editing.sharedKeys.length > 0
                ? editing.sharedKeys.map((row) => ({
                    name: row.name,
                    courseKey: row.courseKey,
                  }))
                : [{ name: '', courseKey: '' }],
          }
        : {}),
    });
  }, [active, editing, initialPlatform]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingCategories(true);
        const res = await categoryApi.getAllCategories();
        if (!cancelled && res.success && res.categories) {
          setCategories(
            res.categories.map((c) => ({
              _id: c._id,
              title: c.title,
              description: c.description,
            }))
          );
        }
      } catch {
        if (!cancelled) toast.error('Failed to load categories');
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  useEffect(() => {
    if (!active || !form.categoryId) {
      setPrograms([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingPrograms(true);
        const res = await programsV2Api.fetchAllPrograms({
          categoryId: form.categoryId,
          status: 'all',
          page: 1,
          limit: 500,
        });
        if (!cancelled && res.success && Array.isArray(res.data?.programs)) {
          setPrograms(
            res.data.programs.map((p) => ({
              _id: p._id,
              title: p.title || p.programId || p._id,
              availableDeliveryModes: extractProgramDeliveryModes(p.availableProgramTypes),
            }))
          );
        } else if (!cancelled) {
          setPrograms([]);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load programs');
      } finally {
        if (!cancelled) setLoadingPrograms(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, form.categoryId]);

  useEffect(() => {
    if (!form.programId) return;
    const program = programs.find((item) => item._id === form.programId);
    if (!program || program.availableDeliveryModes.length === 0) return;

    const allowed = new Set(program.availableDeliveryModes);
    setForm((prev) => {
      const nextModes = prev.deliveryModes.filter((mode) => allowed.has(mode));
      if (nextModes.length === prev.deliveryModes.length) return prev;
      return { ...prev, deliveryModes: nextModes };
    });
  }, [form.programId, programs]);

  const loadIspringCourses = useCallback(async () => {
    if (platform !== 'ispring') return;
    const generation = ++ispringLoadGenerationRef.current;
    try {
      setLoadingCourses(true);
      setLoadingMoreCourses(false);
      setIspringCourses([]);

      let pageToken: string | undefined;
      let accumulated: IspringContentItem[] = [];
      let firstPage = true;

      do {
        const data = await courseKeysApi.listIspringContents(200, pageToken);
        if (generation !== ispringLoadGenerationRef.current) return;

        accumulated = [...accumulated, ...(data.items || [])];
        setIspringCourses(accumulated);

        if (firstPage) {
          setLoadingCourses(false);
          firstPage = false;
          if (data.nextPageToken) setLoadingMoreCourses(true);
        }

        pageToken = data.nextPageToken;
      } while (pageToken);

      if (generation === ispringLoadGenerationRef.current) {
        setLoadingMoreCourses(false);
      }
    } catch {
      if (generation === ispringLoadGenerationRef.current) {
        toast.error('Failed to load iSpring courses');
        setLoadingCourses(false);
        setLoadingMoreCourses(false);
      }
    }
  }, [platform]);

  useEffect(() => {
    if (active && platform === 'ispring') {
      void loadIspringCourses();
    }
  }, [active, platform, loadIspringCourses]);

  const setPlatform = (next: CourseKeyPlatform) => {
    setForm((p) => {
      const updates: Partial<CourseKeyFormValues> = { platform: next };
      if (!editing) {
        const isDefault =
          JSON.stringify(p.contentTabs) === JSON.stringify(defaultContentTabs(p.platform)) ||
          !Object.values(p.contentTabs).some((value) => value.trim());
        if (isDefault) {
          updates.contentTabs = defaultContentTabs(next);
        }
        const isDefaultStructured =
          JSON.stringify(p.contentStructured) === JSON.stringify(defaultContentStructured(p.platform)) ||
          JSON.stringify(p.contentStructured) === JSON.stringify(emptyContentStructured());
        if (isDefaultStructured) {
          updates.contentStructured = defaultContentStructured(next);
        }
        if (next === 'mindtap') {
          const stillDefaultFee =
            p.extensionFees.length === 1 &&
            p.extensionFees[0]?.value === 2 &&
            p.extensionFees[0]?.unit === 'weeks' &&
            p.extensionFees[0]?.amountUsd === 20;
          if (stillDefaultFee) updates.extensionFees = [];
        }
        if (next === 'ispring' && p.extensionFees.length === 0) {
          updates.extensionFees = [{ value: 2, unit: 'weeks', amountUsd: 20 }];
        }
      }
      return { ...p, ...updates };
    });
  };

  const toggleDeliveryMode = (mode: DeliveryModeV2) => {
    setForm((prev) => ({
      ...prev,
      deliveryModes: prev.deliveryModes.includes(mode)
        ? prev.deliveryModes.filter((m) => m !== mode)
        : [...prev.deliveryModes, mode],
    }));
  };

  const toggleCourse = (courseId: string) => {
    if (!courseId) return;
    setForm((prev) => ({
      ...prev,
      ispringCourseIds: prev.ispringCourseIds.includes(courseId)
        ? prev.ispringCourseIds.filter((id) => id !== courseId)
        : [...prev.ispringCourseIds, courseId],
    }));
  };

  const buildPayload = (status: 'draft' | 'active') => {
    const base = {
      platform: form.platform,
      status,
      categoryId: form.categoryId,
      programId: form.programId,
      deliveryModes: form.deliveryModes,
      ispringCourseIds: form.ispringCourseIds,
      rulesHtml: '',
      instructionsHtml: legacyInstructionsFromTabs(form.platform, form.contentTabs),
      contentTabs: form.contentTabs,
      contentStructured: form.contentStructured,
      instructionFiles: form.instructionFiles,
      credentialsPlaceholder: 'tab_block' as const,
      durationValue: form.durationValue,
      durationUnit: form.durationUnit,
      portalUrl: form.portalUrl.trim() || undefined,
    };

    const reactivationFee = {
      inactiveValue: form.reactivationInactiveValue,
      inactiveUnit: form.reactivationInactiveUnit,
      accessValue: form.reactivationAccessValue,
      accessUnit: form.reactivationAccessUnit,
      amountCents: Math.round(form.reactivationAmountUsd * 100),
    };

    if (platform === 'mindtap') {
      return {
        ...base,
        extensionFees: form.extensionFees.map((f) => ({
          value: f.value,
          unit: f.unit,
          amountCents: Math.round(f.amountUsd * 100),
        })),
        reactivationFee,
        sharedKeysEnabled: form.sharedKeysEnabled,
        sharedKeys: normalizeMindtapReleaseRows(form.sharedKeys),
      };
    }

    return {
      ...base,
      extensionFees: form.extensionFees.map((f) => ({
        value: f.value,
        unit: f.unit,
        amountCents: Math.round(f.amountUsd * 100),
      })),
      reactivationFee,
      sharedKeysEnabled: false,
      sharedKeys: [],
    };
  };

  const handleInstructionFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploadingInstructionFile(true);
      const uploaded = await uploadCourseKeyInstructionFile(file);
      setForm((p) => ({
        ...p,
        instructionFiles: [...p.instructionFiles, uploaded],
      }));
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingInstructionFile(false);
    }
  };

  const removeInstructionFile = (index: number) => {
    setForm((p) => ({
      ...p,
      instructionFiles: p.instructionFiles.filter((_, i) => i !== index),
    }));
  };

  const validateSetupLock = (): string | null => {
    if (!form.categoryId) return 'Category is required';
    if (!form.programId) return 'Program is required';
    if (form.deliveryModes.length === 0) return 'Select at least one delivery mode';
    return null;
  };

  const validateSetup = (): string | null => {
    const err = validateSetupLock();
    if (err) return err;
    if (platform === 'ispring' && form.ispringCourseIds.length === 0) {
      return 'Select at least one iSpring course';
    }
    return null;
  };

  const handleSaveSetup = () => {
    const err = validateSetupLock();
    if (err) {
      toast.error(err);
      return;
    }
    setSetupLocked(true);
    toast.success('Setup saved');
  };

  const handleEditSetup = () => {
    setSetupLocked(false);
  };

  const validate = (status: 'draft' | 'active'): string | null => {
    const setupErr = validateSetup();
    if (setupErr) return setupErr;
    if (form.durationValue < 1) return 'Duration must be at least 1';
    if (form.reactivationAccessValue < 1) {
      return 'Reactivation access must be at least 1';
    }
    if (form.reactivationInactiveValue < 1) {
      return 'Reactivation inactive period must be at least 1';
    }
    if (platform === 'mindtap' && status === 'active') {
      const portalUrl = form.portalUrl.trim();
      if (!portalUrl) {
        return 'Cengage portal URL is required before submitting a MindTap config';
      }
      try {
        new URL(portalUrl);
      } catch {
        return 'Enter a valid Cengage portal URL (include https://)';
      }
    }
    if (platform === 'mindtap' && form.sharedKeysEnabled) {
      const sharedErr = validateMindtapReleaseRows(form.sharedKeys);
      if (sharedErr) {
        if (status === 'active') return sharedErr;
        // Draft may save empty shared key rows, but partial rows are still invalid.
        const hasAnyInput = form.sharedKeys.some(
          (row) => row.name.trim().length > 0 || row.courseKey.trim().length > 0
        );
        if (hasAnyInput) return sharedErr;
      }
    } else if (platform === 'mindtap') {
      const hasAnyInput = form.sharedKeys.some(
        (row) => row.name.trim().length > 0 || row.courseKey.trim().length > 0
      );
      if (hasAnyInput) {
        const sharedErr = validateMindtapReleaseRows(form.sharedKeys);
        if (sharedErr) return sharedErr;
      }
    }
    return null;
  };

  const persistSave = async (status: 'draft' | 'active') => {
    try {
      setSaving(true);
      const payload = buildPayload(status);
      let savedId = editing?._id;
      if (editing) {
        const updated = await courseKeysApi.update(editing._id, payload);
        savedId = updated._id;
        toast.success(status === 'active' ? 'Course key submitted' : 'Draft saved');
      } else {
        const created = await courseKeysApi.create(payload);
        savedId = created._id;
        toast.success(status === 'active' ? 'Course key created and submitted' : 'Draft created');
      }
      onSaved();
      if (savedId) {
        onSubmitSuccess({ configId: savedId, platform, status });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (status: 'draft' | 'active') => {
    const err = validate(status);
    if (err) {
      toast.error(err);
      return;
    }
    const coursesChanged =
      Boolean(editing) &&
      platform === 'ispring' &&
      !sameCourseIdSet(form.ispringCourseIds, editing?.ispringCourseIds || []);
    if (coursesChanged) {
      setPendingCourseUpdateStatus(status);
      return;
    }
    void persistSave(status);
  };

  if (!active) return null;

  let step = 1;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            {editing ? 'Edit Course Key' : 'Create Course Keys'}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure a reusable course-key template for enrolled students.
          </p>
        </div>
        {onCancel && (
          <Button variant="ghost" type="button" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>

      <div className="space-y-10">
        <StepSection
          step={step++}
          title="Select the course-key source"
          description="Choose iExamtor (iSpring) or Mindtap. Fields below adapt to your selection."
        >
          <div
            className={cn(
              'grid gap-3 sm:grid-cols-2',
              setupDisabled && 'pointer-events-none opacity-60'
            )}
          >
            <button
              type="button"
              disabled={setupDisabled || Boolean(editing)}
              onClick={() => setPlatform('ispring')}
              className={cn(
                'rounded-xl border p-4 text-left transition-colors',
                platform === 'ispring'
                  ? 'border-foreground bg-muted/60'
                  : 'border-border hover:bg-muted/30',
                editing && 'opacity-70'
              )}
            >
              <strong className="block">iExamtor (iSpring)</strong>
              <span className="mt-1 block text-sm text-muted-foreground">
                Map iSpring courses; students provision login from the portal.
              </span>
            </button>
            <button
              type="button"
              disabled={setupDisabled || Boolean(editing)}
              onClick={() => setPlatform('mindtap')}
              className={cn(
                'rounded-xl border p-4 text-left transition-colors',
                platform === 'mindtap'
                  ? 'border-foreground bg-muted/60'
                  : 'border-border hover:bg-muted/30',
                editing && 'opacity-70'
              )}
            >
              <strong className="block">Mindtap</strong>
              <span className="mt-1 block text-sm text-muted-foreground">
                Use shared program keys, or release course keys per student from the Release tab.
              </span>
            </button>
          </div>
        </StepSection>

        <StepSection
          step={step++}
          title="Select category and program"
          description="One program per template. Delivery modes define which enrollments match."
        >
          <div
            className={cn('space-y-4', setupDisabled && 'pointer-events-none opacity-60')}
          >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="course-key-category">Category</Label>
              <Select
                value={form.categoryId || undefined}
                onValueChange={(value) => {
                  setProgramSearch('');
                  setForm((p) => ({ ...p, categoryId: value, programId: '', deliveryModes: [] }));
                }}
                disabled={loadingCategories}
              >
                <SelectTrigger id="course-key-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category._id} value={category._id}>
                      {category.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.categoryId && (
            <div className="space-y-2">
              <Label>Program</Label>
              {loadingPrograms ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading programs…
                </div>
              ) : programs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No programs found in this category.</p>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={programSearch}
                      onChange={(e) => setProgramSearch(e.target.value)}
                      placeholder="Search programs…"
                      className="pl-9"
                    />
                  </div>
                  <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border p-3">
                    {filteredPrograms.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {programSearch.trim()
                          ? 'No programs match your search.'
                          : 'No programs found in this category.'}
                      </p>
                    ) : (
                      filteredPrograms.map((program) => {
                        const selected = form.programId === program._id;
                        return (
                          <button
                            key={program._id}
                            type="button"
                            onClick={() =>
                              setForm((p) => ({
                                ...p,
                                programId: program._id,
                                deliveryModes:
                                  p.programId === program._id ? p.deliveryModes : [],
                              }))
                            }
                            className={cn(
                              'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors',
                              selected
                                ? 'border-green-600 bg-green-50 text-green-900 dark:bg-green-950/30 dark:text-green-100'
                                : 'border-border hover:bg-muted/50'
                            )}
                          >
                            <span className="truncate">{program.title}</span>
                            {selected && <Badge variant="outline">Selected</Badge>}
                          </button>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Delivery modes</Label>
            {!form.programId ? (
              <p className="text-sm text-muted-foreground">
                Select a program to see its configured delivery modes.
              </p>
            ) : availableDeliveryModeOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No delivery modes are configured for this program yet.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {availableDeliveryModeOptions.map((mode) => (
                  <label key={mode.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.deliveryModes.includes(mode.id)}
                      onCheckedChange={() => toggleDeliveryMode(mode.id)}
                    />
                    {mode.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-2">
            {setupLocked ? (
              <Button type="button" variant="outline" size="sm" onClick={handleEditSetup}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={handleSaveSetup}>
                Save
              </Button>
            )}
          </div>
          </div>
        </StepSection>

        {platform === 'ispring' && (
          <StepSection
            step={step++}
            title="Map iSpring courses"
            description={
              editing
                ? 'Students who already have an iSpring login keep their current courses. Students who have not opened the course key yet receive the courses selected here.'
                : 'Select all iSpring content items students should access.'
            }
          >
            <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {ispringCourses.length > 0
                  ? `${ispringCourses.length} courses loaded${
                      loadingMoreCourses ? '…' : ''
                    }`
                  : 'Available courses'}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={loadingCourses || loadingMoreCourses}
                onClick={() => void loadIspringCourses()}
              >
                Refresh
              </Button>
            </div>
            {loadingCourses ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading courses…
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={ispringCourseSearch}
                    onChange={(e) => setIspringCourseSearch(e.target.value)}
                    placeholder="Search iSpring courses…"
                    className="pl-9"
                  />
                </div>
                <div className="max-h-52 space-y-2 overflow-y-auto rounded-md border p-3">
                  {filteredIspringCourses.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {ispringCourseSearch.trim()
                        ? loadingMoreCourses
                          ? 'Still loading more courses for search…'
                          : 'No courses match your search.'
                        : 'No iSpring courses found.'}
                    </p>
                  ) : (
                    filteredIspringCourses.map((course) => {
                      const id = getContentId(course);
                      const selected = form.ispringCourseIds.includes(id);
                      return (
                        <button
                          key={id || course.title}
                          type="button"
                          onClick={() => toggleCourse(id)}
                          className={cn(
                            'flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors',
                            selected
                              ? 'border-green-600 bg-green-50 text-green-900 dark:bg-green-950/30 dark:text-green-100'
                              : 'border-border hover:bg-muted/50'
                          )}
                        >
                          <span className="truncate">{course.title || id}</span>
                          {selected && <Badge variant="outline">Selected</Badge>}
                        </button>
                      );
                    })
                  )}
                  {loadingMoreCourses ? (
                    <div className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading remaining iSpring courses…
                    </div>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-medium">
                      Selected courses ({selectedIspringCourses.length})
                    </Label>
                    {selectedIspringCourses.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-muted-foreground"
                        onClick={() =>
                          setForm((prev) => ({ ...prev, ispringCourseIds: [] }))
                        }
                      >
                        Clear all
                      </Button>
                    )}
                  </div>
                  {selectedIspringCourses.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No courses selected yet. Pick items from the list above.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {selectedIspringCourses.map((course) => (
                        <li
                          key={course.id}
                          className="flex items-center justify-between gap-3 rounded-md border border-green-600/40 bg-green-50 px-3 py-2 text-sm text-green-900 dark:bg-green-950/30 dark:text-green-100"
                        >
                          <span className="min-w-0 truncate">{course.title}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 shrink-0 p-0 text-green-900 hover:bg-green-100 hover:text-green-950 dark:text-green-100 dark:hover:bg-green-900/40"
                            aria-label={`Remove ${course.title}`}
                            onClick={() => toggleCourse(course.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
            </div>
          </StepSection>
        )}

        <StepSection
          step={step++}
          title="Rules"
          description="Duration, extensions, and reactivation settings for this course key."
        >
          <div className="space-y-6">
              <TimePeriodField
                id="course-key-duration"
                label="Duration"
                description="Initial access duration for enrolled students."
                value={form.durationValue}
                unit={form.durationUnit}
                onValueChange={(durationValue) => setForm((p) => ({ ...p, durationValue }))}
                onUnitChange={(durationUnit) => setForm((p) => ({ ...p, durationUnit }))}
              />

              {platform === 'mindtap' && (
                <div className="max-w-2xl space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="course-key-portal-url">Cengage portal URL</Label>
                    <Input
                      id="course-key-portal-url"
                      type="url"
                      value={form.portalUrl}
                      onChange={(e) => setForm((p) => ({ ...p, portalUrl: e.target.value }))}
                      placeholder="https://login.cengage.com/..."
                      autoComplete="off"
                    />
                    <p className="text-xs text-muted-foreground">
                      Required when submitting. Students see an{' '}
                      <span className="font-medium">Open Cengage Portal</span> button in the course
                      key drawer using this link.
                    </p>
                  </div>

                  <div className="rounded-md border p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <Label htmlFor="shared-keys-toggle">Shared course keys</Label>
                        <p className="text-xs text-muted-foreground">
                          When on, these keys apply to all matching enrollments without per-student
                          release. Students who already have individual keys keep those. Turning
                          this off keeps shared keys as a fallback until a student is re-released.
                        </p>
                      </div>
                      <Switch
                        id="shared-keys-toggle"
                        checked={form.sharedKeysEnabled}
                        onCheckedChange={(checked) =>
                          setForm((p) => ({
                            ...p,
                            sharedKeysEnabled: checked,
                            sharedKeys:
                              checked && p.sharedKeys.length === 0
                                ? [{ name: '', courseKey: '' }]
                                : p.sharedKeys,
                          }))
                        }
                      />
                    </div>

                    {(form.sharedKeysEnabled ||
                      form.sharedKeys.some(
                        (row) => row.name.trim() || row.courseKey.trim()
                      )) && (
                      <div className="space-y-3 border-t pt-3">
                        {!form.sharedKeysEnabled && (
                          <p className="text-xs text-muted-foreground">
                            Shared keys are disabled for new enrollments, but stored keys below still
                            act as a fallback until each student is re-released individually.
                          </p>
                        )}
                        {form.sharedKeys.map((row, idx) => (
                          <div
                            key={`shared-key-${idx}`}
                            className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
                          >
                            <div className="space-y-1">
                              <Label htmlFor={`shared-key-name-${idx}`}>MindTap name</Label>
                              <Input
                                id={`shared-key-name-${idx}`}
                                value={row.name}
                                onChange={(e) =>
                                  setForm((p) => ({
                                    ...p,
                                    sharedKeys: p.sharedKeys.map((item, i) =>
                                      i === idx ? { ...item, name: e.target.value } : item
                                    ),
                                  }))
                                }
                                placeholder="e.g. ICD 10"
                                autoComplete="off"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label htmlFor={`shared-key-value-${idx}`}>Course key</Label>
                              <Input
                                id={`shared-key-value-${idx}`}
                                value={row.courseKey}
                                onChange={(e) =>
                                  setForm((p) => ({
                                    ...p,
                                    sharedKeys: p.sharedKeys.map((item, i) =>
                                      i === idx ? { ...item, courseKey: e.target.value } : item
                                    ),
                                  }))
                                }
                                placeholder="Cengage course key"
                                autoComplete="off"
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Remove shared key row ${idx + 1}`}
                                disabled={form.sharedKeys.length <= 1}
                                onClick={() =>
                                  setForm((p) => ({
                                    ...p,
                                    sharedKeys:
                                      p.sharedKeys.length <= 1
                                        ? [{ name: '', courseKey: '' }]
                                        : p.sharedKeys.filter((_, i) => i !== idx),
                                  }))
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={form.sharedKeys.length >= MINDTAP_MAX_KEYS}
                          onClick={() =>
                            setForm((p) => ({
                              ...p,
                              sharedKeys: [...p.sharedKeys, { name: '', courseKey: '' }],
                            }))
                          }
                        >
                          <Plus className="mr-1 h-4 w-4" /> Add key
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-3 max-w-2xl">
                  <Label className="text-sm font-medium">Extensions</Label>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {platform === 'mindtap'
                        ? 'Paid extensions students can buy after MindTap access expires. The course key stays hidden until staff extends access in Cengage and marks it complete.'
                        : 'Optional paid extensions students can purchase.'}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          extensionFees: [
                            ...p.extensionFees,
                            { value: 4, unit: 'weeks', amountUsd: 40 },
                          ],
                        }))
                      }
                    >
                      <Plus className="mr-1 h-4 w-4" /> Add extension
                    </Button>
                  </div>

                  {form.extensionFees.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No extension options yet. Add rows if students can purchase extra time.
                    </p>
                  ) : (
                    <div className="overflow-x-auto max-w-2xl rounded-md border">
                      <table className="w-full min-w-[320px] text-sm">
                        <thead>
                          <tr className="border-b bg-muted/40 text-left">
                            <th className="px-3 py-2 font-medium">Period</th>
                            <th className="px-3 py-2 font-medium">Fee (USD)</th>
                            <th className="px-3 py-2 font-medium w-16">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.extensionFees.map((row, idx) => (
                            <tr key={`${row.value}-${row.unit}-${idx}`} className="border-b last:border-b-0">
                              <td className="px-3 py-2">
                                <div className="flex gap-2">
                                  <Input
                                    type="number"
                                    min={1}
                                    className="w-20"
                                    aria-label={`Extension value row ${idx + 1}`}
                                    value={row.value}
                                    onChange={(e) => {
                                      const value = parseInt(e.target.value, 10) || 1;
                                      setForm((p) => {
                                        const next = [...p.extensionFees];
                                        next[idx] = { ...next[idx], value };
                                        return { ...p, extensionFees: next };
                                      });
                                    }}
                                  />
                                  <Select
                                    value={row.unit}
                                    onValueChange={(unit) => {
                                      setForm((p) => {
                                        const next = [...p.extensionFees];
                                        next[idx] = {
                                          ...next[idx],
                                          unit: unit as CourseKeyTimeUnit,
                                        };
                                        return { ...p, extensionFees: next };
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="w-[100px]" aria-label={`Extension unit row ${idx + 1}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="days">Days</SelectItem>
                                      <SelectItem value="weeks">Weeks</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <Input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  className="w-28"
                                  aria-label={`Extension fee row ${idx + 1}`}
                                  value={row.amountUsd}
                                  onChange={(e) => {
                                    const amountUsd = parseFloat(e.target.value) || 0;
                                    setForm((p) => {
                                      const next = [...p.extensionFees];
                                      next[idx] = { ...next[idx], amountUsd };
                                      return { ...p, extensionFees: next };
                                    });
                                  }}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Remove extension row ${idx + 1}`}
                                  onClick={() =>
                                    setForm((p) => ({
                                      ...p,
                                      extensionFees: p.extensionFees.filter((_, i) => i !== idx),
                                    }))
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Reactivation</Label>
                {platform === 'mindtap' && (
                  <p className="text-xs text-muted-foreground">
                    Reactivation billing for Mindtap is phase 2; settings are stored for future use.
                  </p>
                )}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="course-key-reactivation-fee">Reactivation fee (USD)</Label>
                    <Input
                      id="course-key-reactivation-fee"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.reactivationAmountUsd}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          reactivationAmountUsd: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Fee charged when a student reactivates after being inactive.
                    </p>
                  </div>
                  <TimePeriodField
                    id="course-key-reactivation-inactive"
                    label="Apply reactivation after"
                    description="Time without login before reactivation is required."
                    value={form.reactivationInactiveValue}
                    unit={form.reactivationInactiveUnit}
                    onValueChange={(reactivationInactiveValue) =>
                      setForm((p) => ({ ...p, reactivationInactiveValue }))
                    }
                    onUnitChange={(reactivationInactiveUnit) =>
                      setForm((p) => ({ ...p, reactivationInactiveUnit }))
                    }
                  />
                  <TimePeriodField
                    id="course-key-reactivation-access"
                    label="Access granted for"
                    description="Access restored after the student pays the reactivation fee."
                    value={form.reactivationAccessValue}
                    unit={form.reactivationAccessUnit}
                    onValueChange={(reactivationAccessValue) =>
                      setForm((p) => ({ ...p, reactivationAccessValue }))
                    }
                    onUnitChange={(reactivationAccessUnit) =>
                      setForm((p) => ({ ...p, reactivationAccessUnit }))
                    }
                  />
                </div>
              </div>
          </div>
        </StepSection>

        <StepSection
          step={step++}
          title="Student content tabs"
          description="Content shown to students in each course key drawer tab."
        >
          {platform === 'mindtap' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Overview</Label>
                <p className="text-xs text-muted-foreground">
                  Intro shown in the student Overview tab before activation steps.
                </p>
                <RichTextEditor
                  value={form.contentTabs.overviewHtml}
                  onChange={(v) =>
                    setForm((p) => ({
                      ...p,
                      contentTabs: { ...p.contentTabs, overviewHtml: v },
                    }))
                  }
                  minHeight={180}
                  enableAI={false}
                  enableImageUrl
                />
              </div>
              <CourseKeyStepBuilder
                label="Instructions steps"
                description={`Use ${MINDTAP_TOKEN} in a step body where the released Cengage course key should appear.`}
                steps={form.contentStructured.instructionsSteps}
                onChange={(instructionsSteps) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, instructionsSteps },
                  }))
                }
              />
              <CourseKeyAlertBuilder
                label="Instruction notices"
                alerts={form.contentStructured.instructionsAlerts}
                onChange={(instructionsAlerts) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, instructionsAlerts },
                  }))
                }
              />
              <CourseKeyStepBuilder
                label="Login steps"
                description="Steps for returning students after the course is activated."
                steps={form.contentStructured.loginStepsSteps}
                onChange={(loginStepsSteps) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, loginStepsSteps },
                  }))
                }
              />
              <CourseKeyStepBuilder
                label="Process steps"
                description="Shown as a vertical step list in the student Process tab."
                steps={form.contentStructured.processSteps}
                onChange={(processSteps) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, processSteps },
                  }))
                }
              />
              <CourseKeyPolicyBuilder
                label="Policy cards"
                policies={form.contentStructured.policyItems}
                onChange={(policyItems) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, policyItems },
                  }))
                }
              />
              <CourseKeyPolicyBuilder
                label="Support cards"
                policies={form.contentStructured.supportItems}
                onChange={(supportItems) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, supportItems },
                  }))
                }
              />
              <div className="space-y-2">
                <Label>Support (legacy HTML fallback)</Label>
                <p className="text-xs text-muted-foreground">
                  Used only when no support cards are configured above.
                </p>
                <RichTextEditor
                  value={form.contentTabs.supportHtml}
                  onChange={(v) =>
                    setForm((p) => ({
                      ...p,
                      contentTabs: { ...p.contentTabs, supportHtml: v },
                    }))
                  }
                  minHeight={140}
                  enableAI={false}
                  enableImageUrl
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Overview</Label>
                <RichTextEditor
                  value={form.contentTabs.overviewHtml}
                  onChange={(v) =>
                    setForm((p) => ({
                      ...p,
                      contentTabs: { ...p.contentTabs, overviewHtml: v },
                    }))
                  }
                  minHeight={180}
                  enableAI={false}
                  enableImageUrl
                />
              </div>
              <CourseKeyStepBuilder
                label="Process steps"
                description="Shown as a step grid in the student Process tab."
                steps={form.contentStructured.processSteps}
                onChange={(processSteps) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, processSteps },
                  }))
                }
                allowWide
              />
              <div className="space-y-2">
                <Label>Login details intro</Label>
                <p className="text-xs text-muted-foreground">
                  Login credentials are rendered automatically in the student drawer. Use this field
                  for intro text only.
                </p>
                <RichTextEditor
                  value={form.contentTabs.loginDetailsHtml}
                  onChange={(v) =>
                    setForm((p) => ({
                      ...p,
                      contentTabs: { ...p.contentTabs, loginDetailsHtml: v },
                    }))
                  }
                  minHeight={140}
                  enableAI={false}
                  enableImageUrl
                />
              </div>
              <CourseKeyPolicyBuilder
                label="Policy cards"
                policies={form.contentStructured.policyItems}
                onChange={(policyItems) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, policyItems },
                  }))
                }
              />
              <CourseKeyPolicyBuilder
                label="Support cards"
                policies={form.contentStructured.supportItems}
                onChange={(supportItems) =>
                  setForm((p) => ({
                    ...p,
                    contentStructured: { ...p.contentStructured, supportItems },
                  }))
                }
              />
              <div className="space-y-2">
                <Label>Support (legacy HTML fallback)</Label>
                <p className="text-xs text-muted-foreground">
                  Used only when no support cards are configured above.
                </p>
                <RichTextEditor
                  value={form.contentTabs.supportHtml}
                  onChange={(v) =>
                    setForm((p) => ({
                      ...p,
                      contentTabs: { ...p.contentTabs, supportHtml: v },
                    }))
                  }
                  minHeight={140}
                  enableAI={false}
                  enableImageUrl
                />
              </div>
            </div>
          )}
          <div className="mt-4 space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">Instruction files</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingInstructionFile}
                    onClick={() => instructionFileInputRef.current?.click()}
                  >
                    {uploadingInstructionFile ? (
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    ) : (
                      <FileUp className="mr-1 h-4 w-4" />
                    )}
                    Upload file
                  </Button>
                  <input
                    ref={instructionFileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,application/pdf,image/jpeg,image/png,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => void handleInstructionFileSelect(e)}
                  />
                </div>
                {form.instructionFiles.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No files attached yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {form.instructionFiles.map((file, idx) => (
                      <li
                        key={`${file.fileUrl}-${idx}`}
                        className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-2 py-1.5 text-sm"
                      >
                        <a
                          href={file.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate text-primary hover:underline"
                        >
                          {file.fileName}
                        </a>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => removeInstructionFile(idx)}
                          aria-label={`Remove ${file.fileName}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
        </StepSection>
      </div>

      <div className="flex flex-wrap justify-end gap-2 border-t pt-6">
        {onCancel && (
          <Button variant="outline" type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button variant="secondary" type="button" onClick={() => void handleSave('draft')} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save as Draft'}
        </Button>
        <Button type="button" onClick={() => handleSave('active')} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save & Submit'}
        </Button>
      </div>

      <AlertDialog
        open={pendingCourseUpdateStatus !== null}
        onOpenChange={(open) => {
          if (!open) setPendingCourseUpdateStatus(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update iSpring courses?</AlertDialogTitle>
            <AlertDialogDescription>
              Students who already have an iSpring login stay on their current courses. Students
              who have not opened the course key yet, and new enrollments, will get this course
              list when they first open it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={() => {
                const status = pendingCourseUpdateStatus;
                setPendingCourseUpdateStatus(null);
                if (status) void persistSave(status);
              }}
            >
              Update courses
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
